import React, { useEffect, useState } from "react";
import { Zap, CheckCircle2 } from "lucide-react";
import { ReadyEarlyModal } from "@/components/ReadyEarlyModal";
import { staffApi } from "@/lib/staffApi";

/**
 * ReadyEarlyButton — reusable trigger for both Calendar and AppointmentDetail.
 *
 * Determines eligibility client-side using ONLY the data we already have,
 * then, once eligible, asks the backend whether a notification has already
 * been sent so we can relabel to a disabled "sent" state.
 *
 * Eligibility:
 *   * Appointment is today
 *   * Booking start is in the future (not started)
 *   * Status is not cancelled / no-show / completed / rejected
 *   * Customer has at least one contact channel (email or phone)
 *   * At least 5 minutes remain before start (else the button is pointless)
 *
 * variant='calendar' → tight circular icon; variant='detail' → labelled button
 */
export function ReadyEarlyButton({ appointment, variant = "detail", onSent }) {
    const [open, setOpen] = useState(false);
    const [alreadySent, setAlreadySent] = useState(null);

    const eligible = isEligible(appointment);

    // Ask the backend if we already sent one (avoids a full modal open then
    // a 409). Only bothers if we're eligible in the first place.
    useEffect(() => {
        if (!eligible) return;
        let cancelled = false;
        staffApi.getReadyEarly(appointment.id)
            .then((r) => { if (!cancelled && r?.sent) setAlreadySent(r); })
            .catch(() => {});
        return () => { cancelled = true; };
    }, [eligible, appointment.id]);

    if (!eligible) return null;

    const label = alreadySent ? "Ready Early sent" : "Ready Early";

    if (variant === "calendar") {
        return (
            <>
                <button type="button"
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(true); }}
                        title={label}
                        aria-label={label}
                        data-testid={`ready-early-btn-cal-${appointment.id}`}
                        className={`h-6 w-6 rounded-full grid place-items-center border transition-colors
                            ${alreadySent
                                ? "border-emerald-400/50 bg-emerald-500/20 text-emerald-200"
                                : "border-gold/50 bg-gold/10 text-gold hover:bg-gold/25"}`}>
                    {alreadySent ? <CheckCircle2 className="h-3 w-3" /> : <Zap className="h-3 w-3" />}
                </button>
                {open && (
                    <ReadyEarlyModal
                        appointment={appointment}
                        onClose={() => setOpen(false)}
                        onSent={(r) => { setAlreadySent(r); setOpen(false); onSent?.(r); }}
                    />
                )}
            </>
        );
    }

    // detail variant
    return (
        <>
            <button type="button"
                    onClick={() => setOpen(true)}
                    data-testid={`ready-early-btn-detail-${appointment.id}`}
                    className={`inline-flex items-center gap-1.5 rounded-full h-9 px-4 text-xs font-semibold uppercase tracking-[0.14em] border transition-colors
                        ${alreadySent
                            ? "border-emerald-400/50 bg-emerald-500/15 text-emerald-100 cursor-default"
                            : "border-gold/60 bg-gold/10 text-gold hover:bg-gold/20"}`}>
                {alreadySent ? <CheckCircle2 className="h-3.5 w-3.5" /> : <Zap className="h-3.5 w-3.5" />}
                {label}
            </button>
            {open && (
                <ReadyEarlyModal
                    appointment={appointment}
                    onClose={() => setOpen(false)}
                    onSent={(r) => { setAlreadySent(r); setOpen(false); onSent?.(r); }}
                />
            )}
        </>
    );
}

function isEligible(appt) {
    if (!appt || !appt.bookingStart) return false;

    const status = (appt.status || "").toLowerCase();
    if (["canceled", "cancelled", "rejected", "no-show", "noshow", "no_show", "checked-out"].includes(status)) {
        return false;
    }

    const cust = appt.customer || {};
    const hasContact = !!(cust.email || cust.phone);
    if (!hasContact) return false;

    let start;
    try {
        start = new Date(appt.bookingStart.replace(" ", "T"));
        if (Number.isNaN(start.getTime())) return false;
    } catch { return false; }

    const now = new Date();
    if (start.toDateString() !== now.toDateString()) return false;
    const minsUntil = (start - now) / 60000;
    if (minsUntil < 5) return false;  // less than 5 min = not worth an alert
    return true;
}
